import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  LinkPicker,
  type LinkOption,
} from '@/components/invoices/LinkPicker';
import { useCurrentOrganization } from '@/hooks/useOrganization';
import { useSites } from '@/hooks/useSites';
import { useTenantSlug } from '@/hooks/useTenantSlug';
import { invoiceClient } from '@/lib/api/ts-rest';
import { cn } from '@/lib/utils';
import {
  Building2,
  Check,
  FileText,
  Loader2,
  Search,
  Upload,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

interface UploadInvoiceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUploadSuccess?: () => void;
  /** Upload a real invoice or a billing detail. */
  kind?: 'INVOICE' | 'DETAIL';
  /** Invoices a detail can be linked to (only used when kind = DETAIL). */
  invoiceOptions?: LinkOption[];
}

export function UploadInvoiceDialog({
  open,
  onOpenChange,
  onUploadSuccess,
  kind = 'INVOICE',
  invoiceOptions = [],
}: UploadInvoiceDialogProps) {
  const isDetail = kind === 'DETAIL';
  const { data: organization } = useCurrentOrganization();
  const { data: sites = [] } = useSites(organization?.id);
  const { tenantSlug } = useTenantSlug();

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedSiteIds, setSelectedSiteIds] = useState<string[]>([]);
  const [reference, setReference] = useState('');
  const [linkedInvoiceId, setLinkedInvoiceId] = useState<string | null>(null);
  const [type, setType] = useState<'PHONE_PLAN' | 'EQUIPMENT'>('PHONE_PLAN');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [siteSearch, setSiteSearch] = useState('');

  const filteredSites = useMemo(() => {
    if (!siteSearch.trim()) return sites;
    const q = siteSearch.toLowerCase();
    return sites.filter((s) => s.name.toLowerCase().includes(q));
  }, [sites, siteSearch]);

  const validateFile = (file: File) => {
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Le fichier est trop volumineux (max 10MB)');
      return false;
    }
    const allowedTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
    ];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Seuls les fichiers PDF et Excel sont acceptés');
      return false;
    }
    return true;
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (validateFile(file)) setSelectedFile(file);
  };

  const handleFileDrop = useCallback((file: File) => {
    if (validateFile(file)) setSelectedFile(file);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) handleFileDrop(files[0]);
    },
    [handleFileDrop]
  );

  const handleSiteToggle = (siteId: string) => {
    setSelectedSiteIds((prev) =>
      prev.includes(siteId)
        ? prev.filter((id) => id !== siteId)
        : [...prev, siteId]
    );
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Veuillez sélectionner un fichier');
      return;
    }
    if (!reference.trim()) {
      toast.error('Veuillez saisir une référence');
      return;
    }
    if (!organization) {
      toast.error('Aucune organisation sélectionnée');
      return;
    }
    if (!tenantSlug) {
      toast.error('Aucun tenant sélectionné');
      return;
    }

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('reference', reference.trim());
      if (!isDetail) formData.append('type', type);
      formData.append('kind', kind);
      formData.append('date', new Date(date).toISOString());
      formData.append('siteIds', JSON.stringify(selectedSiteIds));
      formData.append('tenantId', tenantSlug);

      const response = await invoiceClient.uploadInvoice({
        params: { organizationId: organization.id },
        body: formData,
      });

      if (response.status !== 201) throw new Error('Upload failed');

      // Link the freshly uploaded detail to the chosen invoice, if any.
      if (isDetail && linkedInvoiceId) {
        await invoiceClient.linkDetail({
          params: { organizationId: organization.id, detailId: response.body.id },
          body: { invoiceId: linkedInvoiceId },
        });
      }

      toast.success(
        isDetail ? 'Détail uploadé avec succès' : 'Facture uploadée avec succès'
      );
      onUploadSuccess?.();
      handleClose();
    } catch {
      toast.error(
        isDetail
          ? "Erreur lors de l'upload du détail"
          : "Erreur lors de l'upload de la facture"
      );
    } finally {
      setIsUploading(false);
    }
  };

  const resetForm = () => {
    setSelectedFile(null);
    setSelectedSiteIds([]);
    setReference('');
    setLinkedInvoiceId(null);
    setType('PHONE_PLAN');
    setDate(new Date().toISOString().split('T')[0]);
    setIsDragOver(false);
    setSiteSearch('');
  };

  const handleClose = () => {
    resetForm();
    onOpenChange(false);
  };

  useEffect(() => {
    if (!open) resetForm();
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px] max-h-[90vh] flex flex-col overflow-hidden p-0">
        <DialogHeader className="px-6 pt-6 pb-0 shrink-0">
          <DialogTitle className="text-lg font-bold text-slate-900">
            {isDetail ? 'Uploader un détail de facture' : 'Uploader une facture'}
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-500">
            {isDetail
              ? 'Fichier PDF ou Excel ; vous pouvez le lier à une facture'
              : 'Sélectionnez un fichier PDF ou Excel et renseignez les informations'}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
          {/* File drop zone */}
          <div
            className={cn(
              'relative rounded-xl border-2 border-dashed p-6 text-center transition-all',
              isDragOver
                ? 'border-emerald-400 bg-emerald-50'
                : selectedFile
                  ? 'border-emerald-200 bg-emerald-50/50'
                  : 'border-slate-200 hover:border-slate-300 bg-slate-50/50',
              isUploading && 'opacity-50 pointer-events-none'
            )}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <input
              type="file"
              accept=".pdf,.xlsx,.xls,application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
              onChange={handleFileSelect}
              disabled={isUploading}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            />

            {selectedFile ? (
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-100">
                  <FileText className="h-5 w-5 text-emerald-600" />
                </div>
                <div className="flex-1 min-w-0 text-left">
                  <p className="text-sm font-medium text-slate-900 truncate">
                    {selectedFile.name}
                  </p>
                  <p className="text-xs text-slate-400">
                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedFile(null);
                  }}
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-white hover:text-slate-600"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex h-10 w-10 mx-auto items-center justify-center rounded-xl bg-slate-100">
                  <Upload className="h-5 w-5 text-slate-400" />
                </div>
                <p className="text-sm font-medium text-slate-600">
                  Glissez votre fichier ici
                </p>
                <p className="text-xs text-slate-400">
                  ou cliquez pour sélectionner · Max 10MB
                </p>
              </div>
            )}
          </div>

          {/* Reference (+ Type for a real invoice) */}
          <div
            className={cn('grid gap-3', isDetail ? 'grid-cols-1' : 'grid-cols-2')}
          >
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-slate-500">
                Référence
              </Label>
              <Input
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                placeholder={isDetail ? 'Mai-26 - Détail abonnements' : 'INV-2024-001'}
                disabled={isUploading}
                className="bg-white border-slate-200 text-sm"
              />
            </div>
            {!isDetail && (
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-slate-500">Type</Label>
                <Select
                  value={type}
                  onValueChange={(value: 'PHONE_PLAN' | 'EQUIPMENT') =>
                    setType(value)
                  }
                >
                  <SelectTrigger className="bg-white border-slate-200 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PHONE_PLAN">Forfait mobile</SelectItem>
                    <SelectItem value="EQUIPMENT">Équipement</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* Optional link to a real invoice (detail only) */}
          {isDetail && (
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-slate-500">
                Facture liée (optionnel)
              </Label>
              {linkedInvoiceId ? (
                <div className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm">
                  <span className="truncate text-slate-700">
                    {invoiceOptions.find((o) => o.id === linkedInvoiceId)
                      ?.label ?? 'Facture sélectionnée'}
                  </span>
                  <button
                    type="button"
                    onClick={() => setLinkedInvoiceId(null)}
                    className="shrink-0 text-slate-400 hover:text-slate-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <LinkPicker
                  label="Choisir une facture"
                  items={invoiceOptions}
                  searchPlaceholder="Rechercher une facture…"
                  emptyText="Aucune facture"
                  onSelect={setLinkedInvoiceId}
                />
              )}
            </div>
          )}

          {/* Date */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-slate-500">
              Date de la facture
            </Label>
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              disabled={isUploading}
              className="bg-white border-slate-200 text-sm"
            />
          </div>

          {/* Sites selection */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-medium text-slate-500">
                Centres de facturation
              </Label>
              {selectedSiteIds.length > 0 && (
                <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 rounded-full px-2 py-0.5">
                  {selectedSiteIds.length} sélectionné
                  {selectedSiteIds.length > 1 ? 's' : ''}
                </span>
              )}
            </div>

            <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
              {/* Search within sites */}
              {sites.length > 5 && (
                <div className="relative border-b border-slate-100">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                  <input
                    type="text"
                    value={siteSearch}
                    onChange={(e) => setSiteSearch(e.target.value)}
                    placeholder="Rechercher un site..."
                    className="w-full pl-9 pr-3 py-2.5 text-sm bg-transparent outline-none placeholder:text-slate-400"
                    disabled={isUploading}
                  />
                </div>
              )}

              {/* Sites list */}
              <div className="max-h-40 overflow-y-auto">
                {filteredSites.length === 0 ? (
                  <div className="flex flex-col items-center py-6 text-slate-400">
                    <Building2 className="h-5 w-5 mb-1.5 text-slate-300" />
                    <p className="text-xs">
                      {sites.length === 0
                        ? 'Aucun site disponible'
                        : 'Aucun résultat'}
                    </p>
                  </div>
                ) : (
                  filteredSites.map((site) => {
                    const isSelected = selectedSiteIds.includes(site.id);
                    return (
                      <button
                        key={site.id}
                        type="button"
                        onClick={() => handleSiteToggle(site.id)}
                        disabled={isUploading}
                        className={cn(
                          'flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors',
                          isSelected
                            ? 'bg-emerald-50'
                            : 'hover:bg-slate-50'
                        )}
                      >
                        <div
                          className={cn(
                            'flex h-5 w-5 shrink-0 items-center justify-center rounded border transition-all',
                            isSelected
                              ? 'border-emerald-500 bg-emerald-500'
                              : 'border-slate-300 bg-white'
                          )}
                        >
                          {isSelected && (
                            <Check className="h-3 w-3 text-white" />
                          )}
                        </div>
                        <span
                          className={cn(
                            'text-sm truncate',
                            isSelected
                              ? 'text-emerald-900 font-medium'
                              : 'text-slate-700'
                          )}
                        >
                          {site.name}
                        </span>
                      </button>
                    );
                  })
                )}
              </div>

              {/* Select all / Deselect all */}
              {filteredSites.length > 1 && (
                <div className="border-t border-slate-100 px-3 py-2 flex gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      setSelectedSiteIds(filteredSites.map((s) => s.id))
                    }
                    disabled={isUploading}
                    className="text-xs text-slate-500 hover:text-slate-700 transition-colors"
                  >
                    Tout sélectionner
                  </button>
                  {selectedSiteIds.length > 0 && (
                    <>
                      <span className="text-xs text-slate-300">·</span>
                      <button
                        type="button"
                        onClick={() => setSelectedSiteIds([])}
                        disabled={isUploading}
                        className="text-xs text-slate-500 hover:text-slate-700 transition-colors"
                      >
                        Tout désélectionner
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="px-6 pb-6 pt-3 shrink-0 border-t border-slate-100 gap-2">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isUploading}
            className="rounded-full border-slate-200 text-slate-600"
          >
            Annuler
          </Button>
          <Button
            onClick={handleUpload}
            disabled={!selectedFile || !reference.trim() || isUploading}
            className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm hover:shadow-md transition-all"
          >
            {isUploading ? (
              <span className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Upload en cours...
              </span>
            ) : (
              'Uploader'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
